import { Box, Button, IconButton, Typography, alpha } from '@mui/material'
import { ReactNode, useRef } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { CourseProgressSummary } from 'src/v3/self-paced/types'
import { CourseCard } from './course-card'

export let HorizontalCarousel = ({
  title,
  subtitle,
  courses,
  onCourseClick,
  assignedCourses = [],
  isLocked = false,
  sectionId,
  onNavigate,
  boundTreeStatus,
  courseProgress,
  renderItem,
}: {
  title: string
  subtitle?: string
  courses: any[]
  onCourseClick: (id: string) => void
  assignedCourses?: string[]
  isLocked?: boolean
  sectionId?: string
  onNavigate?: (path: string) => void
  boundTreeStatus?: string
  courseProgress?: Record<string, CourseProgressSummary>
  renderItem?: (item: any) => ReactNode
}) => {
  let scrollRef = useRef<HTMLDivElement | null>(null)

  let scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' })
    }
  }

  let scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' })
    }
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          px: 3,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: 'white' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {sectionId && onNavigate && boundTreeStatus !== 'limited' && (
          <Button
            variant="text"
            color="secondary"
            onClick={() => onNavigate(`/self-paced/section/${sectionId}`)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            View all
          </Button>
        )}
      </Box>
      <Box sx={{ position: 'relative', group: 'carousel' }}>
        {/* Left Arrow */}
        <IconButton
          onClick={scrollLeft}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: alpha('#000', 0.7),
            color: 'white',
            opacity: 0,
            transition: 'opacity 0.3s',
            '.MuiBox-root:hover &': {
              opacity: 1,
            },
            '&:hover': {
              bgcolor: alpha('#000', 0.9),
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        {/* Right Arrow */}
        <IconButton
          onClick={scrollRight}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: alpha('#000', 0.7),
            color: 'white',
            opacity: 0,
            transition: 'opacity 0.3s',
            '.MuiBox-root:hover &': {
              opacity: 1,
            },
            '&:hover': {
              bgcolor: alpha('#000', 0.9),
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>

        {/* Scrollable Container */}
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            pb: 1,
            pl: 3,
            pr: 3,
          }}
        >
          {courses?.map((course: any) =>
            renderItem ? (
              renderItem(course)
            ) : (
              <CourseCard
                key={course.id}
                course={course}
                onCourseClick={onCourseClick}
                isAssigned={assignedCourses.includes(course.id)}
                isLocked={isLocked}
                progress={courseProgress?.[course.id]}
              />
            ),
          )}
        </Box>
      </Box>
    </Box>
  )
}
